import { MeetingConfigViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../../models/AppState';
import { convertDurationToString, copyToClipboard } from '../../../../../models/UiUtils';
import { getDurationText } from '../../../../../scheduleMeeting/UiUtils';
import { Button } from '../../../../components-v2/Button';
import { ButtonGroup } from '../../../../components-v2/ButtonGroup';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownTrigger,
} from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill';
import { brandPrimary, grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { DeleteConfirmation } from '../../../DeleteConfirmation';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ChevronDown } from '../../../svgs/icons/ChevronDown';
import { ClockIcon } from '../../../svgs/icons/ClockIcon';
import { SquareLinkIcon } from '../../../svgs/icons/SquareLinkIcon';
import { BookWithTeammatesModal } from '../BookWithTeammatesModal';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps {
	meeting: MeetingConfigViewModel;
	onDelete(meeting: MeetingConfigViewModel): void;
	onEdit(meeting: MeetingConfigViewModel): void;
	onClone(meeting: MeetingConfigViewModel): void;
}

const MeetingConfigCardBase: React.FC<IProps> = ({ meeting, onDelete, onEdit, onClone }) => {
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = React.useState(false);
	const [isBookWithTeammatesOpen, setIsBookWithTeammatesOpen] = React.useState(false);
	const onDeleteConfirm = (confirmedDelete: boolean) => {
		if (confirmedDelete) {
			onDelete(meeting);
		}
		setIsDeleteConfirmationOpen(false);
	};

	const onCopyLinkClick = useCallback(() => {
		const done = copyToClipboard(meeting.link);
		if (done) {
			toast.success('Copied meeting link to clipboard!');
		} else {
			toast.error('Unable to copy to clipboard.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting]);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.moreMenu)}>
				<Dropdown>
					<DropdownEllipsisTrigger />
					<DropdownContent>
						<DropdownItem onClick={() => onEdit(meeting)}>Edit</DropdownItem>
						<DropdownItem onClick={() => onClone(meeting)}>Clone</DropdownItem>
						<DropdownItem onClick={() => setIsDeleteConfirmationOpen(true)}>Delete</DropdownItem>
					</DropdownContent>
				</Dropdown>
			</div>

			<h3 className={css(styleSheet.header)}>{meeting.name}</h3>
			<div className={css(styleSheet.clockContainer)}>
				<ClockIcon className={css(styleSheet.clockIcon)} fillColor={grayIconFill} />
				<span className={css(styleSheet.clockText)}>{getDurationText(convertDurationToString(meeting.duration))}</span>
				{meeting.category?.name ? (
					<span className={css(styleSheet.categoryPill)}>
						<Pill variant='category' color={meeting.category.color ?? 'blue'} label={meeting.category.name} />
					</span>
				) : null}
			</div>
			{meeting.isPublished ? (
				<div className={css(styleSheet.actions)}>
					<ButtonGroup>
						<Button asChild size='sm' variant='secondary'>
							<a href={meeting.link} target='_blank' rel='noreferrer'>
								View
							</a>
						</Button>
						<Dropdown>
							<DropdownTrigger
								render={
									<Button
										aria-label='More booking options'
										size='sm'
										styles={[styleSheet.caretButton]}
										variant='secondary'
									/>
								}
							>
								<ChevronDown />
							</DropdownTrigger>
							<DropdownContent align='end' styles={[styleSheet.bookingMenu]}>
								<DropdownItem onClick={() => setIsBookWithTeammatesOpen(true)}>Add teammates</DropdownItem>
							</DropdownContent>
						</Dropdown>
					</ButtonGroup>
					<Button onClick={onCopyLinkClick} size='sm' variant='secondary'>
						<SquareLinkIcon fillColor={brandPrimary} />
						<span>Copy Link</span>
					</Button>
				</div>
			) : (
				<div className={css(styleSheet.actions)}>
					<span className={css(styleSheet.unpublished)}>Unpublished</span>
				</div>
			)}
			{meeting.isBusy && <LoadingSpinner type='small' className={css(baseStyleSheet.absoluteCenter)} />}

			<DeleteConfirmation
				bodyText={`Are you sure you want to delete the "${meeting.name}" meeting?`}
				isOpen={isDeleteConfirmationOpen}
				onFinish={onDeleteConfirm}
			/>

			<BookWithTeammatesModal
				isOpen={isBookWithTeammatesOpen}
				meeting={meeting}
				onClose={() => setIsBookWithTeammatesOpen(false)}
			/>
		</div>
	);
};

const MeetingConfigCardAsObserver = observer(MeetingConfigCardBase);
export const MeetingConfigCard = inject(ErrorMessagesViewModelKey)(MeetingConfigCardAsObserver);
